import { useEffect, useMemo, useState } from 'react'

import { zodResolver } from '@hookform/resolvers/zod'
import { i18n } from '@localization'
import { useRoute } from '@react-navigation/native'
import { publishEvent } from '@zet/analytics'
import { BasicDetailsRequest } from '@zet/customer'
import { IRequestData } from '@zet/customer/src/api/functions/customerAdditionStateMachine'
import {
  useAddBasicDetailsToAddCustomer,
  useAddCustomerNextStateWeb,
  useGetPrefilledData,
} from '@zet/customer/src/api/hooks'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { WebDatePicker, colors as zetColors } from '@zet/design-system'
import { Alert } from '@zet/design-system/alert'
import { Icon } from '@zet/design-system/icons'
import { Input } from '@zet/design-system/input'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import { B1, B2, B5, BTN_SM, H5 } from '@zet/design-system/typography'
import { useExperiments } from '@zet/experiments'
import { PINCODE_REGEX } from '@zet/utils/regularExpression'
import moment from 'moment'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import BottomView from '@components/bottomView'
import KeyBoardWrapper from '@components/keyboardWrapper'

import { useModal } from '@hooks/useModal'
import { useNavigation } from '@hooks/useNavigation'

const BasicDetails = () => {
  const [salaryType, setSalaryType] = useState<string>('')
  const { params } = useRoute()
  const { mutate, data, isSuccess, isLoading } =
    useAddBasicDetailsToAddCustomer()
  const {
    customerId,
    setNameAsPerPan,
    setPanAfterVerify,
    setDisableBankNameFieldWhenCash,
    isWebFlow,
  } = useAddCustomerStore((store) => store)

  const { data: prefilledData } = useGetPrefilledData({
    customerId: params?.customerId || customerId,
  })
  const { data: experiments } = useExperiments()
  const { mutate: mutateWithExperiments, isLoading: experimentLoading } =
    useAddCustomerNextStateWeb()

  const validationSchema = useMemo(() => {
    return z
      .object({
        email: z.string().email('Not a valid Email id'),
        pinCode: z
          .string()
          .min(1, { message: 'Field is required!' })
          .length(6, { message: 'Not a valid PIN code' })
          .regex(PINCODE_REGEX, 'Invalid Pin Code'),
        salaryType: z.string({ required_error: 'Please select a type' }),
        dob: z.string(),
        ...(experiments?.UI_isAddCustomerNewFlow
          ? {}
          : {
              panNumber: z
                .string()
                .min(1, { message: 'Field is required!' })
                .regex(
                  new RegExp(/(^([a-zA-Z]{5})([0-9]{4})([a-zA-Z]{1})$)/),
                  'INVALID PAN NUMBER',
                )
                .max(10, 'INVALID PAN NUMBER'),
            }),
      })
      .required()
  }, [experiments?.UI_isAddCustomerNewFlow])

  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
    getValues,
    setValue,
    setError,
    trigger,
  } = useForm<ValidationSchema>({
    mode: 'onChange',
    resolver: zodResolver(validationSchema),
    reValidateMode: 'onChange',
  })

  useEffect(() => {
    if (prefilledData?.modeOfIncome) {
      if (prefilledData?.modeOfIncome === 'Cash') {
        setSalaryType('Cash')
        setValue('salaryType', 'Cash')
      } else if (prefilledData?.modeOfIncome === 'Bank') {
        setSalaryType('Bank')
        setValue('salaryType', 'Bank')
      }
    }
    if (!experiments?.UI_isAddCustomerNewFlow && prefilledData?.panNo) {
      setValue('panNumber', prefilledData?.panNo)
    }
    if (prefilledData?.dob) {
      setValue('dob', moment(prefilledData?.dob).format('YYYY-MM-DD'))
    }
    if (prefilledData?.pincode) {
      setValue('pinCode', prefilledData?.pincode)
    }
    if (prefilledData?.email) {
      setValue('email', prefilledData?.email)
    }
    if (
      !experiments?.UI_isAddCustomerNewFlow &&
      prefilledData &&
      prefilledData?.panNo
    ) {
      trigger()
    }
  }, [prefilledData, experiments?.UI_isAddCustomerNewFlow])

  useEffect(() => {
    if (
      !isWebFlow &&
      experiments?.UI_isAddCustomerNewFlow &&
      params?.customerId
    ) {
      mutateWithExperiments({
        customerId: params?.customerId,
        screenName: 'GET_CURRENT_STATE',
        fullName: params?.customerName,
        phoneNumber: params?.customerPhoneNumber,
      })
    }
  }, [
    experiments?.UI_isAddCustomerNewFlow,
    mutateWithExperiments,
    params?.customerId,
    isWebFlow,
  ])

  const { openModal } = useModal()

  const navigation = useNavigation()

  type ValidationSchema = z.infer<typeof validationSchema>

  useEffect(() => {
    if (isSuccess) {
      if (data?.data?.customerStatus === 6 && data?.data?.panVerified) {
        setNameAsPerPan(data?.data?.nameAsPan)
        setPanAfterVerify(data?.data?.panNo)
        if (getValues('salaryType') === 'Cash') {
          setDisableBankNameFieldWhenCash(true)
        } else {
          setDisableBankNameFieldWhenCash(false)
        }
        navigation.navigate('EmploymentDetailsWF', {
          customerId: params?.customerId,
          customerPhoneNumber: params?.customerPhoneNumber,
          customerName: params?.customerName,
          hashNew: params?.hashNew,
          nameAsPerPan: data?.data?.nameAsPan,
          disableBankName: getValues('salaryType') === 'Cash' ? true : false,
          brandSpecificPartnerId: params?.brandSpecificPartnerId,
          ...(experiments?.UI_isAddCustomerNewFlow
            ? {}
            : { panNumber: data?.data?.panNo }),
        })
      } else if (
        data?.error?.code === 2014 ||
        data?.error?.code === 2015 ||
        data?.error?.code === 2016
      ) {
        publishEvent('validation_error', {
          item_name: 'pan',
          error_message: data?.error?.message,
        })
        setError('panNumber', { type: 'focus', message: data?.error?.message })
      }
    }
  }, [isSuccess])

  const onSubmit = () => {
    const dob = moment(getValues('dob')).format('YYYY-MM-DD')
    const requestBodyData = {
      customerId: params?.customerId || customerId,
      modeOfIncome: getValues('salaryType') === 'Cash' ? 2 : 1,
      dob: dob,
      pinCode: getValues('pinCode'),
      email: getValues('email'),
      ...(experiments?.UI_isAddCustomerNewFlow
        ? { screenName: 'SHOWN_BASIC_DETAILS_SCREEN' }
        : {
            panNo: getValues('panNumber')?.toUpperCase(),
          }),
    }
    if (experiments?.UI_isAddCustomerNewFlow) {
      mutateWithExperiments(requestBodyData as IRequestData)
    } else {
      mutate(requestBodyData as BasicDetailsRequest)
    }
  }

  //  Caching data for recommendation

  return (
    <KeyBoardWrapper
      className="flex-1 bg-white "
      BottomSheet={
        <BottomView
          disabled={!isValid}
          onPress={handleSubmit(onSubmit)}
          loading={isLoading || experimentLoading}
          stepCount={2}
          maxCount={3}
          buttonEvent={{
            eventName: 'click_action',
            payload: {
              item_name: 'add_customer_step_2_continue',
              customer_income_mode: getValues().salaryType,
              customer_dob: getValues().dob,
              customer_pincode: getValues().pinCode,
              customer_email: getValues().email,
              ...(experiments?.UI_isAddCustomerNewFlow
                ? {}
                : { customer_pan: getValues().panNumber }),
            },
          }}
        />
      }
    >
      <Stack className="px-3">
        <H5>{i18n.t('webFlow.basicDetails.pleaseEnter')}</H5>
        {!experiments?.UI_isAddCustomerNewFlow && (
          <>
            <B2 className="text-grays-80 mb-1 mt-3">
              {i18n.t('webFlow.basicDetails.pan')}{' '}
              <B1 className="text-error-100 ml-1">*</B1>
            </B2>
            <Controller
              control={control}
              render={({
                field: { onChange, onBlur, value },
                fieldState: { isTouched },
              }) => (
                <Input
                  autoCapitalize={'characters'}
                  placeholder="Eg: BHWIC2448S"
                  onBlur={(e) => {
                    onBlur(e)
                  }}
                  onChangeText={(value) => {
                    onChange(value)
                  }}
                  value={value}
                  type={
                    errors?.panNumber?.message && isTouched
                      ? 'error'
                      : errors?.panNumber?.message ===
                          i18n.t('webFlow.basicDetails.panExist')
                        ? 'warning'
                        : 'primary'
                  }
                />
              )}
              name="panNumber"
            />
            {errors.panNumber?.message && (
              <B5 className="mt-1 text-red-500">{errors.panNumber?.message}</B5>
            )}
            <Stack className=" mt-4/2 rounded-lg bg-[#ECFFEC] pb-2 ">
              <Alert
                intent="success"
                iconLeft={{
                  name: 'IdCardOutlined',
                  color: zetColors.success[150],
                }}
              >
                {i18n.t('webFlow.basicDetails.why')}
              </Alert>
              <Pressable
                className="border-grays-60 ml-2 w-fit rounded-xl border px-2"
                onPress={() =>
                  openModal('YoutubeVideoModal', {
                    videoId: 'QzxZaK0DWxQ',
                    videoData: {
                      en: { videoId: 'QzxZaK0DWxQ' },
                      hi: { videoId: '6x2KKRKLUGI' },
                    },
                  })
                }
                eventName="click_action"
                eventPayload={{ item_name: 'add_customer_why_pan_video' }}
              >
                <XStack className="items-center space-x-2">
                  <Icon type="VideoOutlined" size={16} />
                  <BTN_SM className="text-grays-60">
                    {i18n.t('webFlow.basicDetails.watch')}
                  </BTN_SM>
                </XStack>
              </Pressable>
            </Stack>
          </>
        )}

        <Stack className="mt-2">
          <B2 className=" text-grays-80 mb-1">
            {i18n.t('webFlow.basicDetails.income')}{' '}
            <B1 className="text-error-100 ml-1">*</B1>
          </B2>
          <XStack>
            <Controller
              control={control}
              render={({ field: { onChange, onBlur } }) => (
                <XStack>
                  <Pressable
                    onBlur={onBlur}
                    onPress={() => {
                      onChange('Bank')
                      setSalaryType('Bank')
                    }}
                  >
                    <XStack
                      className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Bank' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                    >
                      {salaryType !== 'Bank' ? (
                        <Icon type="BankAccount" size={36}></Icon>
                      ) : (
                        <Icon type="BankAccountWithColor" size={36}></Icon>
                      )}
                      <B2 className="ml-4/2 text-grays-60">
                        {i18n.t('customerDetails.bank')}
                      </B2>
                    </XStack>
                  </Pressable>
                  <Pressable
                    onBlur={onBlur}
                    onPress={() => {
                      onChange('Cash')
                      setSalaryType('Cash')
                    }}
                  >
                    <XStack
                      className={`px-4/2 mr-3 items-center rounded-xl border py-2
             ${
               'Cash' !== salaryType
                 ? 'border-grays-40'
                 : 'border-primary-100 bg-grays-20'
             }
            `}
                    >
                      {salaryType !== 'Cash' ? (
                        <Icon type="Cash" size={36}></Icon>
                      ) : (
                        <Icon type="CashWithColor" size={36}></Icon>
                      )}
                      <B2 className="ml-4/2 text-grays-60">
                        {i18n.t('customerDetails.cash')}
                      </B2>
                    </XStack>
                  </Pressable>
                </XStack>
              )}
              name="salaryType"
            />
          </XStack>
          {errors.salaryType?.message ? (
            <Stack className="mt-2">
              <B5 className="text-error-100">{errors.salaryType?.message}</B5>
            </Stack>
          ) : null}
        </Stack>
        <Stack className="mt-3">
          <B2 className="text-grays-80 mb-1">
            {i18n.t('webFlow.basicDetails.dob')}
            <B1 className="text-error-100 ml-1">*</B1>
          </B2>
          <Controller
            control={control}
            name="dob"
            render={({ field: { value, onChange } }) => (
              <>
                <Icon
                  type="CalenderOutlined"
                  color={zetColors.grays[50]}
                  className="absolute left-2 mt-6 pl-2 pt-1"
                  size={31}
                ></Icon>
                {/* {!value && (
                    <Stack className="absolute left-6 mt-6 pt-1">
                      <SH1 className="text-grays-50">DD/MM/YYYY</SH1>
                    </Stack>
                  )} */}
                <WebDatePicker
                  onChange={(v) => {
                    onChange(v)
                  }}
                  value={value}
                />
              </>
            )}
          />
        </Stack>
        <Stack className="mt-3">
          <B2 className="text-grays-80 mb-2">
            {i18n.t('webFlow.basicDetails.pinCode')}
            <B1 className="text-error-100 pl-1">*</B1>
          </B2>
          <Controller
            control={control}
            render={({
              field: { onChange, onBlur, value },
              fieldState: { isTouched },
            }) => (
              <Input
                maxLength={6}
                iconLeft={
                  <Icon
                    type="LocationPointerOutlined"
                    color={zetColors.grays[50]}
                  ></Icon>
                }
                placeholder="Eg: 560035"
                onBlur={(e) => {
                  onBlur(e)
                }}
                onChangeText={(value) => {
                  onChange(value)
                }}
                value={value}
                type={
                  errors?.pinCode?.message && isTouched ? 'error' : 'primary'
                }
                maxLength={6}
              />
            )}
            name="pinCode"
          />
          {errors.pinCode?.message && (
            <B5 className="mt-1 text-red-500">{errors.pinCode?.message}</B5>
          )}
        </Stack>
        <B2 className="text-grays-80 mb-1 mt-3">
          {i18n.t('webFlow.basicDetails.email')}{' '}
          <B1 className="text-error-100 ml-1">*</B1>
        </B2>
        <Controller
          control={control}
          render={({
            field: { onChange, onBlur, value },
            fieldState: { isTouched },
          }) => (
            <Input
              placeholder="Eg: username@email.com"
              onBlur={(e) => onBlur(e)}
              onChangeText={(value) => {
                onChange(value)
              }}
              value={value}
              type={errors?.email?.message && isTouched ? 'error' : 'primary'}
            />
          )}
          name="email"
        />
        {errors.email?.message && (
          <B5 className="mt-1 text-red-500">{errors.email?.message}</B5>
        )}
        <Stack className="bg-grays-30 mt-5 h-[1] flex-grow"></Stack>
      </Stack>
    </KeyBoardWrapper>
  )
}

export default BasicDetails
